import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { NumberFormatter } from '@repo/toolkit/number'
import { toFullName } from '@repo/toolkit/to-full-name'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import { CheckMarkIcon, CreditCardIcon, EmailIcon, PenIcon, UploadIcon } from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { toast } from 'sonner'

import { blockAffectedStaffPas } from '../../../api/affected-staffs/block-affected-staff-pas'
import {
  getAffectedStaffTotalSalariesWithoutPas,
  getAffectedStaffTotalSalariesWithPas
} from '../../../api/affected-staffs/get-affected-staff-salary-total'
import { unblockAffectedStaffPas } from '../../../api/affected-staffs/unblock-affected-staff-pas'
import { validateAffectedStaffsSalaries } from '../../../api/affected-staffs/validate-affected-staff-salary-total'
import { API, constructApiUrl } from '../../../api/apis.config'
import { travelExpensesSendEmailToStaffs } from '../../../api/travel-expenses/send-email-to-staffs'
import FilterDrawer from '../../../components/elements/drawers/filter-drawer'
import DynamicDataTable, { DynamicTableRef } from '../../../components/elements/dynamic-table'
import { countFilters } from '../../../components/elements/dynamic-table/utils/countFilters'
import UserNavBar from '../../../components/elements/nav-bar'
import { getAffectedStaffSalaryColumns } from '../../../components/pages/affected-staffs/affected-staff-salary.utils'
import { AffectedStaffsSalaryFilters } from '../../../components/pages/affected-staffs/affected-staffs-salary-filters'
import SendEmailForm from '../../../components/pages/candidates/send-email-form'
import { Paths } from '../../../lib/constants/path'
import { useDownloadDocuments } from '../../../lib/hooks/use-download-documents'
import { useFilters } from '../../../lib/hooks/use-filters'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { useUrl } from '../../../lib/hooks/use-url'
import { StaffPaymentStatusEnum } from '../../../lib/types/enums/affected-staff/staff-payment-status.enum'
import { RoleEnum } from '../../../lib/types/enums/users/role.enum'
import {
  AffectedStaffMap,
  AffectedStaffSalaryFilter
} from '../../../lib/types/models/affectedStaff/affected-staff'
import { StaffRequestProperties } from '../../../lib/types/models/staff-request/properties.staff-request'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import {
  transformAffectedStaffSalaryFiltersToUrl,
  transformUrlToAffectedStaffSalaryFilters
} from '../../../lib/utils/filters-mapping/affected-staff-salary'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'

export function AffectedStaffSalariesListPage() {
  const { navigateWithEvent } = useNavigator()
  const { downloadDocuments } = useDownloadDocuments()
  const tableRef = useRef<DynamicTableRef<AffectedStaffMap['SALARY']>>(null)
  const [isTableLoading, setIsTableLoading] = useState(false)
  const { readPagination } = useUrl()
  const [search, setSearch] = useState(() => readPagination().q)
  const { modalState, toggleModal, closeModel, selectedData } = useModalManager<
    'filters' | 'send-email',
    AffectedStaffMap['SALARY'][]
  >(['filters', 'send-email'])

  const { selectedFilter, filter, onFilterChange, resetFilters, onSelectedFilterChange } =
    useFilters<AffectedStaffSalaryFilter>({
      // Validated transfers are hidden by default, they stay reachable through the filters
      initialFilters: {
        paymentStatuses: [StaffPaymentStatusEnum.PENDING, StaffPaymentStatusEnum.EXPORTED]
      },
      options: { allowSearchParam: true },
      transformFilterToUrl: transformAffectedStaffSalaryFiltersToUrl,
      transformUrlToFilter: transformUrlToAffectedStaffSalaryFilters
    })

  const { mutate: blockAffectedStaffPasMutation } = useMutation({
    mutationFn: blockAffectedStaffPas,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Le PAS a été bloqué avec succès')
      tableRef.current?.refetch()
    }
  })

  const { mutate: unblockAffectedStaffPasMutation } = useMutation({
    mutationFn: unblockAffectedStaffPas,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Le PAS a été débloqué avec succès')
      tableRef.current?.refetch()
    }
  })

  const { mutateAsync: validateAffectedStaffsSalariesMutation } = useMutation({
    mutationFn: validateAffectedStaffsSalaries,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Les salaires ont été validés avec succès')
      tableRef.current?.refetch()
    }
  })

  const sendEmailToStaffsMutation = useMutation({
    mutationFn: travelExpensesSendEmailToStaffs,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Les emails ont été envoyés avec succès')
      closeModel('send-email')
    }
  })

  const { data: totalSalariesWithPas } = useQuery({
    queryKey: ['affected-staff-salaries-total-with-pas', JSON.stringify(selectedFilter), search],
    queryFn: () => getAffectedStaffTotalSalariesWithPas(selectedFilter, search)
  })

  const { data: totalSalariesWithoutPas } = useQuery({
    queryKey: [
      'affected-staff-salaries-total-without-pas',
      JSON.stringify(selectedFilter),
      search
    ],
    queryFn: () => getAffectedStaffTotalSalariesWithoutPas(selectedFilter, search)
  })

  const handlePasBlockedChange = (checked: boolean, affectedStaff: AffectedStaffMap['SALARY']) => {
    if (checked) blockAffectedStaffPasMutation(affectedStaff.id)
    else unblockAffectedStaffPasMutation(affectedStaff.id)
  }
  const getAvailableAffectedStaffIds = (affectedStaffs: AffectedStaffMap['SALARY'][]) => {
    return affectedStaffs.filter((as) => as.pasBlocked).map((as) => as.id)
  }
  return (
    <div className="flex flex-col gap-4">
      <UserNavBar title={'Paiement et suivi des honoraires encadrants'} icon={CreditCardIcon} />

      <DynamicDataTable<AffectedStaffMap['SALARY'], AffectedStaffSalaryFilter>
        onLoadingChange={setIsTableLoading}
        ref={tableRef}
        filters={selectedFilter}
        filtersLength={countFilters(selectedFilter)}
        title={'Paiement encadrants'}
        selectable
        disableRow={({ pasBlocked }) => !pasBlocked}
        columns={getAffectedStaffSalaryColumns({
          role: RoleEnum.ADMIN,
          handlePasBlockedChange
        })}
        allowSearchParam
        onSearchChange={setSearch}
        onRowClick={(row, event) =>
          navigateWithEvent(
            Paths.AFFECTED_STAFFS_VIEW_PAGE.replace(':id', row.id.toString()),
            event
          )
        }
        onFiltersClick={() => toggleModal('filters')}
        endpoint={API.affectedStaffs.getAffectedStaffsSalaries.url}
        permissions={{ delete: false }}
        actions={[
          {
            type: 'multiple',
            icon: UploadIcon,
            label: 'Export excel des salaires avant PAS',
            variant: 'ghost',
            fn: async (affectedStaffs) => {
              const ids = getAvailableAffectedStaffIds(affectedStaffs)
              if (ids.length === 0) {
                toast.error("Aucun des encadrants sélectionnés n'a le PAS bloqué.")
                return
              }
              await downloadDocuments({
                endpoint: constructApiUrl(
                  API.affectedStaffs.exportAffectedStaffsSalariesBeforePasToExcel.url,
                  null
                ),
                ids
              })
            }
          },
          {
            type: 'multiple',
            icon: PenIcon,
            label: 'Editer les virements',
            variant: 'ghost',
            fn: async (affectedStaffs) => {
              // The PAS is required for French staff only, UK staff are paid without it
              const message =
                affectedStaffs.length === 1
                  ? 'Le PAS n’a pas encore été saisi, édition du virement impossible.'
                  : 'Le PAS de certains encadrants n’a pas encore été saisi, édition des virements impossible.'

              const isPasMissing = affectedStaffs.some(
                (as) => as.pas == null && !StaffRequestProperties.isForeignStaff(as.staffRequest)
              )
              if (isPasMissing) {
                toast.error(message)
                return
              }
              const ids = getAvailableAffectedStaffIds(affectedStaffs)
              if (ids.length === 0) {
                toast.error("Aucun des encadrants sélectionnés n'a le PAS bloqué.")
                return
              }
              await downloadDocuments({
                ids,
                endpoint: API.affectedStaffs.exportAffectedStaffsSalariesToXml.url
              })
              tableRef.current?.refetch()
            }
          },
          {
            type: 'multiple',
            icon: CheckMarkIcon,
            label: 'Virement validé',
            variant: 'ghost',
            fn: async (affectedStaffs) => {
              const ids = getAvailableAffectedStaffIds(affectedStaffs)
              if (ids.length === 0) {
                toast.error("Aucun des encadrants sélectionnés n'a le PAS bloqué.")
                return
              }
              await validateAffectedStaffsSalariesMutation(ids)
            }
          },
          {
            type: 'multiple',
            icon: EmailIcon,
            label: 'Email groupé',
            variant: 'ghost',
            fn: (affectedStaffs) => {
              const ids = getAvailableAffectedStaffIds(affectedStaffs)
              if (ids.length === 0) {
                toast.error("Aucun des encadrants sélectionnés n'a le PAS bloqué.")
                return
              }

              toggleModal(
                'send-email',
                affectedStaffs.filter((as) => ids.includes(as.id))
              )
            }
          }
        ]}
        footerclassName="bg-primary-light"
        footerCells={[
          {
            accessorKey: 'id',
            value: (
              <Typography variant="caption" color={tailwindColors.primary.DEFAULT}>
                Total
              </Typography>
            )
          },
          {
            accessorKey: 'totalSalary',
            value: (
              <div className="flex flex-col gap-2 py-2">
                {totalSalariesWithoutPas?.totals.map(({ currency, total }) => (
                  <Badge
                    key={currency}
                    variant="orange"
                    text={NumberFormatter.formatCurrency(total, currency)}
                  />
                ))}
              </div>
            )
          },
          {
            accessorKey: 'viewData.totalSalaryAfterPas',
            value: (
              <div className="flex flex-col gap-2 py-2">
                {totalSalariesWithPas?.totals.map(({ currency, total }) => (
                  <Badge
                    key={currency}
                    variant="orange"
                    text={NumberFormatter.formatCurrency(total, currency)}
                  />
                ))}
              </div>
            )
          }
        ]}
      />

      <FilterDrawer
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        isLoading={isTableLoading}
        onClose={() => closeModel('filters')}
        onReset={resetFilters}
        onApply={() => onSelectedFilterChange({ ...filter })}
        onFilterChange={onFilterChange}
      >
        <AffectedStaffsSalaryFilters
          type={'advanced'}
          filter={filter}
          onFilterChange={onFilterChange}
        />
      </FilterDrawer>

      {selectedData && (
        <Modal
          isOpen={modalState['send-email']}
          title={'Envoyer un email'}
          icon={EmailIcon}
          size="medium"
          hideActions
          onCancel={() => closeModel('send-email')}
        >
          <SendEmailForm
            className={MODAL_BODY_CLASSNAME}
            defaultValues={{
              emails: [...new Set(selectedData.flatMap((data) => data.staff.user.email))],
              subject: '',
              body: ''
            }}
            labels={selectedData.map((data) => {
              const email = data.staff.user?.email
              const label = `${toFullName(data.staff.firstName, data.staff.lastName, { options: { mode: 'lastname-firstname' } })} - ${email}`
              return label
            })}
            buttonsConfig={modalButtonConfig({
              onCancel: () => closeModel('send-email'),
              isLoading: sendEmailToStaffsMutation.isPending,
              onConfirm: (values) => {
                return sendEmailToStaffsMutation.mutate({
                  ...values,
                  emails: selectedData.map((affectedStaff) => affectedStaff.staff.user.email)
                })
              }
            })}
          />
        </Modal>
      )}
    </div>
  )
}
